import { Link, useLocation } from "react-router-dom";
import { BrandWordmark } from "@/components/BrandWordmark";
import { resetConsent } from "@/lib/consent";
import { analyticsEnabled } from "@/lib/analytics";
import { clarityEnabled } from "@/lib/clarity";

function BunnyMark() {
  return (
    <span style={{
      width: 26, height: 26,
      display: "grid", placeItems: "center",
      borderRadius: 9,
      background: "linear-gradient(150deg, var(--lp-lilac-soft), var(--lp-blush-soft))",
      border: "1px solid var(--lp-border)",
      flexShrink: 0,
    }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 11.5c0-3.5 1.2-7 1.8-7.2.6-.2 1 .8 1.2 2 .2 1.2.2 2.7.2 4.2" stroke="#2B1F3D" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M16 11.5c0-3.5-1.2-7-1.8-7.2-.6-.2-1 .8-1.2 2-.2 1.2-.2 2.7-.2 4.2" stroke="#2B1F3D" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M5.5 16.5c0-3.2 2.9-5.5 6.5-5.5s6.5 2.3 6.5 5.5c0 2.6-2.4 3.5-6.5 3.5s-6.5-.9-6.5-3.5z" stroke="#2B1F3D" strokeWidth="1.4"/>
        <circle cx="10" cy="16.4" r=".7" fill="#2B1F3D"/>
        <circle cx="14" cy="16.4" r=".7" fill="#2B1F3D"/>
      </svg>
    </span>
  );
}

export function LandingFooter() {
  // Same reasoning as LandingNav: #how / #workflow / #integrations only exist on
  // the landing page, so on every other route they must become real links to "/"
  // rather than anchors that quietly do nothing.
  const isLanding = useLocation().pathname === "/";
  const anchor = (hash: string) => (isLanding ? hash : `/${hash}`);

  return (
    <footer className="lp-footer">
      <div className="lp-container">
        <div className="lp-foot-grid" data-reveal>
          <div className="lp-foot-col">
            <div style={{ display: "inline-flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
              <BunnyMark />
              <BrandWordmark style={{ fontFamily: "var(--lp-sans)", fontWeight: 600, fontSize: 18, letterSpacing: "-0.02em" }} />
            </div>
            <p style={{ color: "var(--lp-plum-500)", maxWidth: "36ch", margin: 0, lineHeight: 1.65, fontSize: 14 }}>
              Simple scheduling for people who value their time - and everyone else&apos;s.
            </p>
          </div>
          <div className="lp-foot-col">
            <h2 className="lp-foot-heading">Product</h2>
            <a href={anchor("#how")}>How it works</a>
            <a href={anchor("#workflow")}>Workflow types</a>
            <a href={anchor("#integrations")}>Integrations</a>
            <Link to="/pricing">Pricing</Link>
            {/* Site-wide internal link so the comparison page is reachable from
                every route — a page only the sitemap knows about gets crawled
                far later than one that is actually linked. Footer rather than
                nav: the nav is six balanced items and a seventh would change
                the layout. */}
            <Link to="/calendly-alternative">Calendly alternative</Link>
          </div>
          {/* Company + Legal are wrapped so the mobile footer can stack them in
              one grid cell (see .lp-foot-stack). On desktop the wrapper is
              `display: contents`, so the 4-column grid sees the two columns
              directly and its layout is unchanged. */}
          <div className="lp-foot-stack">
          <div className="lp-foot-col">
            <h2 className="lp-foot-heading">Company</h2>
            <Link to="/about">About</Link>
            {/* Was an "#field-notes" anchor pointing at a section that doesn't
                exist on the page. Now the actual blog. */}
            <a href="/blog/">Blog</a>
            <a href="/docs/">Docs</a>
            {/* Sat under the legal column while it was called "Quietly", which
                was vague enough to absorb anything. Under an accurate heading
                it clearly isn't legal, and it belongs next to Docs anyway. */}
            <Link to="/docs/zoom">Zoom Docs</Link>
            {/* "Careers" was an "#careers" anchor with no matching section — it
                scrolled nowhere. Removed rather than stubbed: a link to a page
                that doesn't exist is a dead end for readers and a wasted crawl
                for search engines. Restore it when there's a careers page. */}
            <Link to="/contact">Contact</Link>
          </div>
          <div className="lp-foot-col">
            {/* Was "Quietly" — on-brand, but it told nobody what was inside.
                People scanning a footer for Terms look for "Legal". */}
            <h2 className="lp-foot-heading">Legal</h2>
            <Link to="/privacy">Privacy Policy</Link>
            {/* "Status" (#status) and "Accessibility" (#a11y) were also anchors
                with no target on the page. Same reasoning as Careers above. */}
            <Link to="/terms">Terms</Link>
            {/* Consent has to stay withdrawable, so re-open the banner on demand. Hidden when
                no analytics ID is configured, since then there is nothing to manage. */}
            {(analyticsEnabled || clarityEnabled) && (
              <button type="button" className="lp-foot-linkbtn" onClick={resetConsent}>
                Manage cookies
              </button>
            )}
          </div>
          </div>
        </div>
        <div className="lp-foot-bottom">
          <div>© 2026 BunnyCal · Made with care.</div>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ fontFamily: "var(--lp-mono)", letterSpacing: ".15em", textTransform: "uppercase", fontSize: 10.5 }}>v 2.6</span>
            <span>All systems calm</span>
            <span style={{
              display: "inline-block", width: 8, height: 8,
              borderRadius: 999,
              background: "var(--lp-sage)",
              boxShadow: "0 0 0 3px var(--lp-sage-soft)",
            }} />
          </div>
        </div>
      </div>
    </footer>
  );
}
